import { createCompositeString } from '@nucleus/lib-shape';
import { BlurHash, convertFocalPointToPositionCss } from '@nucleus/react-components';
import { BlendMode, Filter, Texture } from '@nucleus/types/media/blending';
import { ImageScale, ImageTile } from '@nucleus/types/media/image';
import { MediaWeb } from '@nucleus/types/web';
import classNames from 'classnames';
import { sortBy as _sortBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { nucleusClass } from '../../lib/class';
import { buildFadeCss } from '../../lib/utilities';
import { BackgroundVideoPlayer } from './BackgroundVideoPlayer';

interface BackgroundMediaProps {
  background?: MediaWeb;
  baseClassNames?: Array<string>;
  className?: string;
  fallbackColor?: string;
  onClick?: () => void;
}

export const BackgroundComposition = ({
  background,
  baseClassNames,
  className,
  fallbackColor = 'transparent',
  ...props
}: BackgroundMediaProps): JSX.Element => {
  const getBlockClass = (name: string): string => {
    if (baseClassNames === undefined) {
      return '';
    }

    return baseClassNames
      .filter((baseClassName) => baseClassName.length > 0)
      .map((baseClassName) =>
        nucleusClass(
          createCompositeString(
            [baseClassName, name].filter((part) => part.length > 0),
            '-'
          )
        )
      )
      .join(' ');
  };

  const renderVideo = doesBackgroundHaveValidVideo(background);

  return (
    <Wrapper
      onClick={props.onClick}
      className={classNames(getBlockClass(''), className)}
      background={background}
      fallbackColor={fallbackColor}
    >
      <ColorLayer
        className={classNames(getBlockClass('color'))}
        background={background}
        fallbackColor={fallbackColor}
      />
      <MediaLayer className={classNames(getBlockClass('asset'))} background={background}>
        <SwitchImageLayer image={background?.image} />
        {renderVideo && <VideoLayer video={background.video} />}
      </MediaLayer>
      <OpacityLayer
        className={classNames(getBlockClass('opacity'))}
        background={background}
        fallbackColor={fallbackColor}
      />
      <FilterColorLayer background={background} />
      <FilterColor2Layer className={classNames(getBlockClass('filter'))} background={background} />
      <FadeLayer className={classNames(getBlockClass('fade'))} background={background} />
      <TextureLayer className={classNames(getBlockClass('texture'))} background={background} />
    </Wrapper>
  );
};

interface SwitchImageLayerProps {
  image: MediaWeb['image'];
}

const SwitchImageLayer = ({ image }: SwitchImageLayerProps) => {
  const [imageAsyncState, imageStateProps] = useImageState();

  if (image === undefined) {
    return null;
  }
  if (image.src === undefined || image.src === '') {
    return null;
  }

  if (doesBackgroundImageTile(image) === true) {
    return <TiledImageLayer image={image} />;
  }

  const sortedSets = _sortBy(image.srcSet ?? [], 'dimensions.width');
  const srcSet = sortedSets.map((srcSet) => `${srcSet.src} ${srcSet.dimensions.width}w`).join(', ');

  const largestDimensions = sortedSets[sortedSets.length - 1]?.dimensions;
  const isImageLoaded = imageAsyncState === 'fulfilled';

  return (
    <>
      {image.blurHash && isImageLoaded === false && <BlurHash hash={image.blurHash} />}
      <ImageLayer
        image={image}
        src={image.src}
        srcSet={srcSet}
        dimensions={image.dimensions ?? largestDimensions}
        isLoaded={isImageLoaded === true}
        alt={image.altText ?? ''}
        {...imageStateProps}
      />
    </>
  );
};

type ImageState = 'pending' | 'fulfilled' | 'error';

const useImageState = (): [
  ImageState,
  React.ImgHTMLAttributes<HTMLImageElement> & React.RefAttributes<HTMLImageElement>,
] => {
  const [state, setState] = useState<ImageState>('pending');
  const ref = useRef<HTMLImageElement | null>(null);

  const onLoad = React.useCallback(() => setState('fulfilled'), []);

  const onError = React.useCallback(() => setState('error'), []);

  useEffect(() => {
    const img = ref.current;

    if (img === null) {
      return;
    }

    // If the image is already loaded from cache, set the state to fulfilled
    if (img.complete && img.naturalWidth > 0) {
      setState('fulfilled');
    }
  }, []);

  return [state, { onLoad: onLoad, onError: onError, ref: ref }];
};

const doesBackgroundImageTile = (image: NonNullable<MediaWeb['image']>): boolean => {
  if ([undefined, 'cover'].includes(image.scale) === true) {
    return false;
  }

  return [undefined, 'none'].includes(image.tile) === false;
};

const doesBackgroundHaveValidVideo = (
  background?: MediaWeb
): background is MediaWeb & { video: NonNullable<MediaWeb['video']> } => {
  return background?.video?.src !== undefined;
};

const Layer = styled.div<BackgroundMediaProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
`;

const MediaLayer = styled(Layer)<BackgroundMediaProps>``;

const ImageTileToBackgroundRepeatCss: Record<ImageTile, string> = {
  none: 'no-repeat',
  repeat: 'repeat',
  'repeat-x': 'repeat-x',
  'repeat-y': 'repeat-y',
};

const ImageScaleToBackgroundSizeCss: Record<ImageScale, string> = {
  none: 'auto',
  cover: 'cover',
  contain: 'contain',
};

const ImageScaleToObjectFitCss: Record<ImageScale, string> = {
  none: 'unset',
  cover: 'cover',
  contain: 'contain',
};

const TiledImageLayer = styled(Layer)<{ image: NonNullable<MediaWeb['image']> }>`
  background-image: url(${(props) => props.image?.src ?? ''});
  background-size: ${(props) => ImageScaleToBackgroundSizeCss[props.image?.scale ?? 'cover'] ?? 'cover'};
  background-position: ${(props) => convertFocalPointToPositionCss(props.image?.focalPoint)};
  background-repeat: ${(props) => ImageTileToBackgroundRepeatCss[props.image?.tile ?? 'none'] ?? 'no-repeat'};
`;

const setDimensions = (props: ImageProps) => props.image?.scale === 'none' && props.dimensions !== undefined;

interface ImageProps {
  image: NonNullable<MediaWeb['image']>;
  dimensions?: { width: number; height: number };
  isLoaded: boolean;
}

const ImageLayer = styled.img<ImageProps>`
  position: absolute;
  height: ${(props) => (setDimensions(props) ? `${props.dimensions?.height}px` : '100%')};
  width: ${(props) => (setDimensions(props) ? `${props.dimensions?.width}px` : '100%')};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: all;

  object-fit: ${(props) => ImageScaleToObjectFitCss[props.image?.scale ?? 'cover'] ?? 'cover'};
  object-position: ${(props) =>
    props.image?.scale === 'contain' ? '50% 50%' : convertFocalPointToPositionCss(props.image?.focalPoint)};
  opacity: 0;

  ${({ isLoaded }) =>
    isLoaded &&
    css`
      opacity: 1;
      transition: opacity 0.3s ease-in-out;
    `}
`;

const VideoLayer = styled(BackgroundVideoPlayer)<BackgroundMediaProps>``;

const ColorLayer = styled(Layer)<BackgroundMediaProps>`
  background-color: ${getColorFromProps};
  pointer-events: all;
`;

const OpacityLayer = styled(Layer)<BackgroundMediaProps>`
  background-color: ${getColorFromProps};
  opacity: ${(props) => 1 - (props.background?.blending?.opacity ?? 1)}; // we want to invert the opacity
`;

const FilterColorLayer = styled(Layer)<BackgroundMediaProps>`
  background-color: var(--color-dark);
  opacity: 0;
`;

const FilterColor2Layer = styled(Layer)<BackgroundMediaProps>`
  // This is used for duotone effects
  background-color: var(--color-6);
  opacity: 0;
`;

const FadeLayer = styled(Layer)<BackgroundMediaProps>`
  opacity: 0;
`;

const TextureLayer = styled(Layer)<BackgroundMediaProps>`
  opacity: 0;
`;

function getColorFromProps(props: BackgroundMediaProps) {
  return props.background?.color?.hex ?? props.fallbackColor;
}

const BackgroundMediaBlendModeToCss: Record<BlendMode, any> = {
  ['none']: css``,
  ['multiply']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: multiply;
    }
  `,
  ['screen']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: screen;
    }
  `,
  ['overlay']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: overlay;
    }
  `,
  ['darken']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: darken;
    }
  `,
  ['lighten']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: lighten;
    }
  `,
  ['color-dodge']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: color-dodge;
    }
  `,
  ['color-burn']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: color-burn;
    }
  `,
  ['hard-light']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: hard-light;
    }
  `,
  ['soft-light']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: soft-light;
    }
  `,
  ['difference']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: difference;
    }
  `,
  ['exclusion']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: exclusion;
    }
  `,
  ['hue']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: hue;
    }
  `,
  ['saturation']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: saturation;
    }
  `,
  ['color']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: color;
    }
  `,
  ['luminosity']: css`
    ${MediaLayer} {
      opacity: 1;
      mix-blend-mode: luminosity;
    }
  `,
};

const BackgroundMediaFilterToCss: Record<Filter, any> = {
  none: css``,
  sepia: css`
    ${MediaLayer} {
      filter: sepia(100%);
    }
  `,
  duotone: css`
    ${MediaLayer} {
      filter: grayscale(100%);
    }
    ${FilterColorLayer} {
      opacity: 1;
      mix-blend-mode: lighten;
    }
    ${FilterColor2Layer} {
      opacity: 1;
      mix-blend-mode: multiply;
    }
  `,
  grayscale: css`
    ${MediaLayer} {
      filter: grayscale(100%);
    }
  `,
  invert: css`
    ${MediaLayer} {
      filter: invert(100%);
    }
  `,
  blur: css`
    ${MediaLayer} {
      filter: blur(10px);
    }
  `,
  muted: css`
    ${MediaLayer} {
      filter: saturate(80%);
    }
  `,
  vibrant: css`
    ${MediaLayer} {
      filter: saturate(120%);
    }
  `,
  darken: css`
    ${MediaLayer} {
      filter: brightness(80%);
    }
  `,
  brighten: css`
    ${MediaLayer} {
      filter: brightness(120%);
    }
  `,
};

const BackgroundTextureToCss: Record<Texture, any> = {
  none: css``,
  'dots-small': css`
    ${TextureLayer} {
      background-image: radial-gradient(var(--color-section-background-texture) 1px, transparent 1px),
        radial-gradient(var(--color-section-background-texture) 1px, transparent 1px);
      background-position:
        0 0,
        5px 5px;
      background-size: 10px 10px;
      mix-blend-mode: overlay;
      opacity: 1;
    }
  `,
  'grid-small': css`
    ${TextureLayer} {
      background-image: linear-gradient(var(--color-section-background-texture) 1px, transparent 1px),
        linear-gradient(90deg, var(--color-section-background-texture) 1px, transparent 1px);
      background-size: 10px 10px;
      mix-blend-mode: overlay;
      opacity: 1;
    }
  `,
};

const Wrapper = styled.div<BackgroundMediaProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  user-select: none;
  pointer-events: none;

  ${(props) => BackgroundMediaBlendModeToCss[props.background?.blending?.blendMode ?? 'none']}
  ${(props) => BackgroundMediaFilterToCss[props.background?.blending?.filter ?? 'none']}
  ${(props) => BackgroundTextureToCss[props.background?.blending?.texture ?? 'none']}

  // Fade Styles
  ${FadeLayer} {
    ${(props) =>
      buildFadeCss(
        props.background?.color?.hex ?? props.fallbackColor,
        props.background?.blending?.fade,
        props.background?.blending?.fadeIntensity
      )}
  }
`;
